import Carousel from 'nsw-design-system/src/components/card-carousel/carousel.js';
import { fetchConfig } from '../../scripts/aem.js';
import './facebook-feed.scss';
import 'nsw-design-system/src/components/card-carousel/_carousel.scss';
import 'nsw-design-system/src/components/card/_card.scss';
import { div } from '../../scripts/dom-helpers.js';

/**
 * Converts standard Facebook URLs to mobile web URLs and appends
 * the #no_universal_links fragment. The fragment matches an exclusion
 * rule in Facebook's official apple-app-site-association file,
 * forcing standard mobile web rendering in the browser rather than
 * launching the native mobile app (Universal Links) and causing Safari
 * to display a cancelled navigation / address invalid error. [SWS-2067]
 *
 * @param {string} url - The Facebook URL to convert.
 * @returns {string} The URL with the host rewritten to m.facebook.com
 *   and #no_universal_links appended, or the original value if it is
 *   empty/invalid.
 */
export function formatFacebookUrl(url) {
  if (!url || typeof url !== 'string') return url;

  if (!/^(?:https?:\/\/)?(?:[a-z0-9-]+\.)?facebook\.com/i.test(url)) {
    return url;
  }

  let formattedUrl = url.replace(/^https:\/\/(?:www\.)?facebook\.com\//i, 'https://m.facebook.com/');

  if (!formattedUrl.includes('#')) {
    formattedUrl += '#no_universal_links';
  } else if (!formattedUrl.includes('no_universal_links')) {
    formattedUrl = formattedUrl.replace(/#.*$/, '#no_universal_links');
  }

  return formattedUrl;
}

/**
 * Detects if the current user agent is mobile/tablet, and returns
 * '_self' to prevent Safari from opening a new tab and failing with
 * an "invalid address" error when deep-linking. Otherwise returns '_blank'.
 *
 * @returns {string} The HTML anchor target attribute value.
 */
export function getLinkTarget() {
  const isMobile = /iPhone|iPad|iPod|Android/i.test(window.navigator.userAgent);
  return isMobile ? '_self' : '_blank';
}

/**
 * Returns the permalink URL for a Facebook post, constructing one from
 * the post's composite id ("<pageId>_<postId>") when permalink_url is
 * absent from the API response, and formatting it for mobile web. [SWS-2067]
 *
 * @param {Object} post - The Facebook post object.
 * @returns {string} A formatted mobile Facebook URL for the post.
 */
export function getPermalinkUrl(post) {
  if (post?.permalink_url) {
    return formatFacebookUrl(post.permalink_url);
  }
  // Construct URL from composite id "<pageId>_<postId>"
  if (post?.id && typeof post.id === 'string' && post.id.includes('_')) {
    const [pageId, postId] = post.id.split('_');
    if (pageId && postId) {
      return formatFacebookUrl(`https://www.facebook.com/${pageId}/posts/${postId}`);
    }
  }
  return formatFacebookUrl('https://www.facebook.com/');
}

const FB_PLACEHOLDER_KEY = 'facebookapi';
const FB_PAGE_ID = 'facebookPageid';
const LOCAL_DUMMY_JSON = '/src/blocks/facebook-feed/fbApi.json';
// Pure text, video, reel
const LOCAL_TEST_1 = '/src/blocks/facebook-feed/testFbPost1.json';
// Image without description, video without description
const LOCAL_TEST_2 = '/src/blocks/facebook-feed/testFbPost2.json';

async function getFacebookApiPath(userInputPageId) {
  const isLocalhost = window.location.hostname === 'localhost';

  try {
    const response = await Promise.resolve(fetchConfig());
    if (isLocalhost && (!response || !response?.[FB_PLACEHOLDER_KEY] || !response?.[FB_PAGE_ID])) {
      return LOCAL_DUMMY_JSON;
    }
    if (response.facebooktest === '1') return LOCAL_TEST_1;
    if (response.facebooktest === '2') return LOCAL_TEST_2;
    if (userInputPageId) {
    // SWS-1495 Do some sanitisation on input
      return `${response[FB_PLACEHOLDER_KEY]}?pageid=${userInputPageId.trim()}&numberofposts=20`;
    }

    return `${response[FB_PLACEHOLDER_KEY]}?pageid=${response[FB_PAGE_ID].trim()}&numberofposts=20`;
  } catch (error) {
    console.error('Error fetching placeholders:', error);
    return undefined;
  }
}

/**
 * Functions to detect the FB post type
 */
const FacebookPostType = {
  isImage: (post) => post?.permalink_url?.includes('post') && post?.full_picture,
  isVideo: (post) => post?.permalink_url?.includes('video'),
  isReel: (post) => post?.permalink_url?.includes('reel'),
  isText: (post) => post?.permalink_url?.includes('post') && !post?.full_picture && post?.message,
  inWhitelist: (post) => post?.full_picture || post?.message,
};

/**
 * Determines if a post should use square layout
 * @param {Object} post - The Facebook post object
 * @returns {boolean} True if post should use square layout
 */
function isSquareLayout(post) {
  if (FacebookPostType.isReel(post)) return true;
  if (FacebookPostType.isImage(post) && !post.message) return true;
  if (FacebookPostType.isVideo(post) && !post.message) return true;
  return false;
}

const PLAY_BUTTON = `<svg width="93" height="65" viewBox="0 0 93 65" fill="none" xmlns="http://www.w3.org/2000/svg">
<rect x="1.0814" y="1.0814" width="90.8372" height="62.1802" rx="15.1395" fill="black" fill-opacity="0.3" stroke="white" stroke-width="2.16279"/>
<path d="M61.8251 29.6954L39.9948 17.1489C38.0795 16.0486 35.686 17.4244 35.686 19.6251V44.7181C35.686 46.9189 38.0795 48.2946 39.9948 47.1943L61.8251 34.6478C63.7404 33.5474 63.7404 30.7959 61.8251 29.6954Z" fill="white"/>
</svg>`;
const FACEBOOK_ICON_SVG = '<svg width="24" height="24" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32"><path fill="#1578f2" d="M32 16.098C32 7.207 24.837 0 16 0S0 7.207 0 16.098C0 24.133 5.851 30.793 13.5 32V20.751H9.437v-4.653H13.5v-3.547c0-4.035 2.389-6.263 6.043-6.263 1.751 0 3.582.314 3.582.314v3.962h-2.018c-1.988 0-2.607 1.241-2.607 2.514v3.02h4.438l-.709 4.653h-3.728V32c7.649-1.208 13.5-7.867 13.5-15.902z"/></svg>';
const ICON_GROUP = `
  <div class="card-icon">
  ${FACEBOOK_ICON_SVG}
  </div>
  <span class="material-icons nsw-material-icons" focusable="false" aria-hidden="true">east</span>`;

function createReelMediaContainer(post) {
  return `<div class="nsw-card__image __square reel_background_container">
    <img class="reel_background" src="${post?.full_picture ?? ''}" alt="Facebook post image">
    <img class="reel_picture" src="${post?.full_picture ?? ''}" alt="Facebook post image"></img>
    ${PLAY_BUTTON}
    </div>`;
}

function createImageContainer(post, isSquare) {
  return `<div class="nsw-card__image ${isSquare ? '__square' : ''}">
  <img src="${post?.full_picture ?? ''}" alt="Facebook post image">
  </div>`;
}

function createVideoContainer(post, isSquare) {
  return `<div class="nsw-card__image ${isSquare ? '__square' : ''}">
  <img src="${post?.full_picture ?? ''}" alt="Facebook post image">
  ${PLAY_BUTTON}
  </div>`;
}

function createMediaContainer(post) {
  const isSquare = isSquareLayout(post);

  if (FacebookPostType.isReel(post)) return createReelMediaContainer(post);
  if (FacebookPostType.isImage(post)) return createImageContainer(post, isSquare);
  if (FacebookPostType.isVideo(post)) return createVideoContainer(post, isSquare);

  return '';
}

function createTextContent(permalinkUrl, message) {
  const target = getLinkTarget();
  return `
    <div class="nsw-card__content text_content">
      <div class="nsw-card__copy text">
        <p>
          <a href="${permalinkUrl}" target="${target}" rel="noopener noreferrer">${message ?? ''}</a>
        </p>
      </div>
          ${ICON_GROUP}
    </div>
  `;
}

function createEmptyContent(permalinkUrl) {
  const target = getLinkTarget();
  return `
    <div class="nsw-card__content __square_pic_adjust">
      <div class="nsw-card__copy">
        <p>
          <a href="${permalinkUrl}" target="${target}" rel="noopener noreferrer"></a>
        </p>
      </div>
        ${ICON_GROUP}
    </div>
  `;
}

function createImgWithDescriptionContent(permalinkUrl, message) {
  const target = getLinkTarget();
  return `
  <div class="nsw-card__content">
    <div class="nsw-card__copy">
      <p>
          <a href="${permalinkUrl}" target="${target}" rel="noopener noreferrer">${message ?? ''}</a>
      </p>
    </div>
      ${ICON_GROUP}
  </div>
`;
}

/**
 * Creates the card content HTML
 * @param {Object} post - The Facebook post object
 * @returns {string} HTML string for the card content
 */
function createCardContent(post) {
  const isSquare = isSquareLayout(post);
  const isTextPost = FacebookPostType.isText(post);
  const message = FacebookPostType.isReel(post) ? '' : (post?.message ?? '');
  const permalinkUrl = getPermalinkUrl(post);

  if (isSquare) return createEmptyContent(permalinkUrl);
  if (isTextPost) return createTextContent(permalinkUrl, message);
  return createImgWithDescriptionContent(permalinkUrl, message);
}

/**
 * Creates a Facebook post card HTML
 * @param {Object} post - The Facebook post object
 * @returns {string} Complete HTML string for the post card
 */
function createPostCard(post) {
  const isTextPost = FacebookPostType.isText(post);
  const mediaContainer = createMediaContainer(post);
  const cardContent = createCardContent(post);

  return `
    <div class="nsw-col nsw-col-md-6 nsw-col-lg-4">
      <div class="nsw-card ${isTextPost ? '' : 'nsw-card--highlight'}">
        ${mediaContainer}
        ${cardContent}
      </div>
    </div>
  `;
}

function buildCarouselElement(post, index) {
  const mobileLiTemp = document.createElement('div');
  const mediaContainer = createMediaContainer(post);
  const cardContent = createCardContent(post);
  mobileLiTemp.innerHTML = `
  <li class="nsw-carousel__item" aria-roledescription="slide" aria-label="${index + 1} of 4" data-index="0" aria-current="true" style="width: 247.3px;">
  <div class="nsw-card nsw-card--headline">
    ${mediaContainer}
    ${cardContent}
  </div>
  </li>
  `;
  return mobileLiTemp.firstElementChild;
}

function buildCarousel(posts) {
  const mobileFacebookTemp = document.createElement('div');
  mobileFacebookTemp.innerHTML = `
  <div class="nsw-carousel js-carousel nsw-carousel--loaded" data-drag="on" aria-roledescription="carousel" aria-label="Card carousel">
          <div class="nsw-carousel__wrapper js-carousel__wrapper">
            <ol class="nsw-carousel__list" style="transform: translateX(0px);">
            </ol>
          </div>
          <nav>
            <ul>
              <li>
                <button aria-controls="nsw-carousel__list" aria-label="Show previous items" class="nsw-carousel__control nsw-carousel__control--prev js-carousel__control" disabled="true">
                <svg class="nsw-icon" viewBox="0 0 20 20">
                  <title>Show previous items</title>
                  <polyline points="13 2 5 10 13 18" fill="none" stroke="currentColor" stroke-linecap="square" stroke-linejoin="square" stroke-width="2"></polyline>
                </svg>
                </button>
              </li>
              <li>
                <button aria-controls="nsw-carousel__list" aria-label="Show next items" class="nsw-carousel__control nsw-carousel__control--next js-carousel__control">
                <svg class="nsw-icon" viewBox="0 0 20 20">
                  <title>Show next items</title>
                  <polyline points="7 2 15 10 7 18" fill="none" stroke="currentColor" stroke-linecap="square" stroke-linejoin="square" stroke-width="2"></polyline>
                </svg>
                </button>
              </li>
            </ul>
          </nav>
        <div class="sr-only js-carousel__aria-live" aria-live="polite" aria-atomic="true"></div>
        <ol class="nsw-carousel__navigation js-carousel__navigation">
          <li class="nsw-carousel__nav-item js-carousel__nav-item nsw-carousel__nav-item--selected"><button><span class="sr-only">1</span></button></li>
          <li class="nsw-carousel__nav-item js-carousel__nav-item"><button><span class="sr-only">2</span></button></li>
          <li class="nsw-carousel__nav-item js-carousel__nav-item"><button><span class="sr-only">3</span></button></li>
        </ol></div>`;
  const list = mobileFacebookTemp.querySelector('.nsw-carousel__list');
  posts.forEach((post, index) => {
    const li = buildCarouselElement(post, index);
    list.append(li);
  });
  return mobileFacebookTemp.firstElementChild;
}

function updateFacebookFeed(posts, block) {
  if (!posts?.length || !block) return;

  const fbPostsEl = posts.map(createPostCard);
  const fbPostMobile = buildCarousel(posts);

  const nswGrid = document.createElement('div');
  nswGrid.classList.add('nsw-grid');
  nswGrid.innerHTML = fbPostsEl.join('');

  // Clear and update the block
  block.textContent = '';
  block.append(nswGrid);
  block.append(fbPostMobile);
  const carouselEl = block.querySelector('.js-carousel');
  const carousel = new Carousel(carouselEl);
  carousel.init();
}

async function fetchFacebookPosts(block, userInputPageId) {
  const fbApi = await getFacebookApiPath(userInputPageId);
  if (!fbApi) {
    return;
  }

  try {
    const fbResponse = await fetch(fbApi, {
      method: 'GET',
    });

    if (!fbResponse.ok) {
      throw new Error(
        `Facebook API request failed: ${fbResponse.statusText}`,
      );
    }

    const fbPosts = await fbResponse.json();

    if (!fbPosts?.length || !fbPosts?.[0]?.posts) {
      throw new Error('No Facebook posts found');
    }

    const posts = JSON.parse(fbPosts[0]?.posts ?? '[]');

    // Update the UI with the fetched posts
    updateFacebookFeed(posts.filter(FacebookPostType.inWhitelist).slice(0, 3), block);
  } catch (error) {
    console.error('Error fetching Facebook API:', error);
  }
}

export default async function decorate(block) {
  const userInputPageId = block?.textContent?.trim() ?? '';
  // Create a loading state
  const fbTimeline = div({ class: 'nsw-grid' });
  fbTimeline.innerHTML = `<div class="nsw-col nsw-col-md-12">
    <p>Loading Facebook posts...</p>
  </div>`;

  block.textContent = '';
  block.append(fbTimeline);

  // [SWS-606] intentionally not await to prevent blocking
  fetchFacebookPosts(block, userInputPageId);
}
